<template>
    <div
        class="rounded-xl border border-taupe-200 bg-white p-5 overflow-hidden"
        data-testid="swap-source-target-panel"
    >
        <div
            class="-mx-5 -mt-5 mb-5 bg-taupe-50 border-b border-taupe-200 px-5 py-4"
        >
            <h2 class="text-xl font-medium text-taupe-800">Target</h2>
            <p v-if="notServed" class="mt-1 text-sm text-taupe-500">
                A temporary swap is in place on this environment.
            </p>
            <p v-else class="mt-1 text-sm text-taupe-500">
                A temporary swap is replacing this environment's targeting for
                this flag.
            </p>
        </div>

        <div
            role="status"
            class="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
            data-testid="swap-source-notice"
        >
            <!-- The top banner already says this environment is swapped, until when, and offers Revert, so this only says what is shown. -->
            <p v-if="notServed" data-testid="swap-not-served-note">
                <span class="font-medium">{{ sourceName }}</span> has no
                published target for this flag, so the swap leaves it out and
                this environment keeps serving its own. Targets are read-only
                during an environment swap.
            </p>
            <p v-else-if="snapshot" data-testid="swap-snapshot-note">
                Target values and rules are a snapshot of
                <span class="font-medium">{{ sourceName }}</span
                ><template v-if="snapshotTakenAt">
                    as of <zen-date-time :value="snapshotTakenAt" />.</template
                ><template v-else>.</template>
                Targets are read-only during an environment swap.
            </p>
            <template v-else>
                <p>
                    Values come from
                    <span class="font-medium">{{ sourceName }}</span>
                    until
                    <zen-date-time :value="expiresAt" />. The swap reverts
                    automatically after that.
                </p>
                <p class="mt-2">
                    Targeting for this environment can't be edited while the
                    swap runs. Only the value is served here, so the rules below
                    are {{ sourceName }}'s and are shown for reference.
                </p>
            </template>
        </div>

        <div class="mt-6 space-y-6">
            <div>
                <h3 class="text-base font-medium text-taupe-900">
                    Served value
                </h3>
                <p
                    v-if="!target"
                    class="mt-1 text-sm text-taupe-500"
                    data-testid="swap-source-no-target"
                >
                    {{ notServed ? 'This environment' : sourceName }}
                    has no published target for this flag.
                </p>
                <p
                    v-else
                    class="mt-1 text-sm font-medium text-taupe-800"
                    data-testid="swap-source-value"
                >
                    {{ target.value?.data?.display }}
                </p>
            </div>

            <div
                v-if="rules.length > 0"
                class="space-y-4"
                data-testid="swap-source-rules"
            >
                <h3 class="text-base font-medium text-taupe-900">
                    Target Rules
                </h3>
                <context-rule
                    v-for="rule in rules"
                    :key="rule.ulid"
                    :rule="rule"
                    :flag="flag"
                    :target="target"
                    :values="values"
                    :true-value="trueValue"
                    :false-value="falseValue"
                    :read-only="true"
                    :draggable="false"
                />
            </div>

            <div v-if="rollout" data-testid="swap-source-rollout">
                <h3 class="text-base font-medium text-taupe-900">
                    Percentage Rollout
                </h3>
                <p
                    class="mt-1 text-sm text-taupe-800"
                    data-testid="swap-rollout-summary"
                >
                    {{ rollout.percentage }}% of contexts get
                    <span class="font-medium">{{
                        rollout.target?.value?.data?.display
                    }}</span
                    >, the rest get the value above.
                    <span
                        v-if="rollout.status === 'paused'"
                        class="text-taupe-500"
                    >
                        {{
                            notServed
                                ? 'The rollout is paused.'
                                : 'The rollout was paused when the swap started.'
                        }}
                    </span>
                </p>

                <div
                    v-if="rollout.rules.length > 0"
                    class="mt-4 space-y-4"
                    data-testid="swap-rollout-rules"
                >
                    <h4 class="text-sm font-medium text-taupe-700">
                        Rules for the rollout value
                    </h4>
                    <context-rule
                        v-for="rule in rollout.rules"
                        :key="rule.ulid"
                        :rule="rule"
                        :flag="flag"
                        :target="rollout.target"
                        :values="values"
                        :true-value="trueValue"
                        :false-value="falseValue"
                        :read-only="true"
                        :draggable="false"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ContextRule from '@/screens/_components/project/ContextRule.vue'

export default {
    name: 'SwapSourceTargetPanel',
    components: {
        ContextRule,
    },
    props: {
        flag: {
            type: Object,
            required: true,
        },
        sourceName: {
            type: String,
            required: true,
        },
        expiresAt: {
            type: String,
            required: true,
        },
        target: {
            type: Object,
            default: null,
        },
        rules: {
            type: Array,
            default: () => [],
        },
        // True when the swap copied the source's targeting (ZEN-2030). False for an older swap that only overrides the value.
        snapshot: {
            type: Boolean,
            default: false,
        },
        // When the swap copied the source's targeting, as an ISO timestamp. Only a snapshot swap has one.
        snapshotTakenAt: {
            type: String,
            default: null,
        },
        // True when the swap leaves this flag out, so what is shown is this environment's own targeting, not the source's (ZEN-2043).
        notServed: {
            type: Boolean,
            default: false,
        },
        // The source's rollout: { target, rules, percentage, status }, or null when it was not rolling out.
        rollout: {
            type: Object,
            default: null,
        },
        values: {
            type: Array,
            default: () => [],
        },
        trueValue: {
            type: Object,
            default: null,
        },
        falseValue: {
            type: Object,
            default: null,
        },
    },
}
</script>
